// Prompt AS (2026-09-25): pantalla del CAMBIO 2 de AJ (el backend ya estaba publicado).
// Al desactivar a alguien (o sobre alguien ya inactivo) muestra qué turnos futuros tiene
// y deja elegir: liberarlos como VACANTES para cubrir, o solo desactivar.
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";

const overlay = { position: "fixed", inset: 0, background: "rgba(10,10,10,0.7)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 220 };
const box = { background: "#fff", borderRadius: 12, padding: 20, width: "100%", maxWidth: 520, maxHeight: "88vh", overflowY: "auto" };

const fmtDate = (d) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;

// mode: "desactivar" (usuario activo) | "liberar" (usuario ya inactivo)
export function DeactivateUserModal({ user, mode, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [removeFromSites, setRemoveFromSites] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/users/${user.id}/deactivate-preview`)
      .then(({ data }) => setPreview(data))
      .catch((e) => { toast.error(formatApiError(e)); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);

  const report = (res) => {
    if (res && res.released_shifts !== undefined) {
      toast.success(`${res.released_shifts} turno(s) pasaron a vacantes · rondas reasignadas: ${res.patrols_assigned}, sin asignar: ${res.patrols_unassigned}`);
    }
  };

  const run = async (kind) => {
    if (busy) return;
    setBusy(true);
    try {
      if (kind === "liberar_inactivo") {
        const { data } = await api.post(`/users/${user.id}/release-shifts`, null, { params: { remove_from_sites: removeFromSites } });
        report(data);
      } else {
        const { data } = await api.post(`/users/${user.id}/deactivate`, { mode: kind, remove_from_sites: removeFromSites });
        toast.success(`${user.name} quedó desactivado`);
        report(data);
      }
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div style={overlay} onClick={onClose} data-testid="deactivate-user-modal-overlay">
      <div style={box} onClick={(e) => e.stopPropagation()} data-testid="deactivate-user-modal">
        <h3 style={{ margin: "0 0 8px", fontSize: 16, fontWeight: 900, color: "#0A0A0A" }}>
          {mode === "liberar" ? `Liberar los turnos futuros de ${user.name}` : `Desactivar a ${user.name}`}
        </h3>
        {!preview ? (
          <div style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>Cargando...</div>
        ) : (
          <>
            <p style={{ fontSize: 13, margin: "0 0 8px" }} data-testid="deactivate-user-summary">
              Tiene <b>{preview.future_shifts}</b> turno(s) desde ahora y <b>{preview.pending_patrols}</b> ronda(s) pendiente(s).
              {preview.sites_count > 0 && <> Figura en el personal de <b>{preview.sites_count}</b> sede(s)/objetivo(s).</>}
            </p>
            {preview.shifts_detail?.length > 0 && (
              <div style={{ maxHeight: 180, overflowY: "auto", background: "var(--invict-bone-2)", borderRadius: 6, padding: "6px 10px", marginBottom: 10 }}>
                {preview.shifts_detail.map((s, i) => (
                  <div key={i} style={{ fontSize: 12, padding: "2px 0" }}>
                    {fmtDate(s.date)} {s.time_from}–{s.time_to} · {s.client_name}{s.site_name ? ` · ${s.site_name}` : ""}
                  </div>
                ))}
              </div>
            )}
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 700, margin: "4px 0 12px", cursor: "pointer" }}>
              <input type="checkbox" checked={removeFromSites} onChange={(e) => setRemoveFromSites(e.target.checked)} data-testid="deactivate-user-remove-sites" />
              Sacarlo también del personal de sus sedes
            </label>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {mode === "liberar" ? (
                <button type="button" disabled={busy} onClick={() => run("liberar_inactivo")} className="btn-primary" style={{ padding: 10 }} data-testid="deactivate-user-release">
                  {busy ? "Procesando..." : "Liberar turnos futuros (quedan como vacantes)"}
                </button>
              ) : (
                <>
                  <button type="button" disabled={busy} onClick={() => run("liberar")} className="btn-primary" style={{ padding: 10 }} data-testid="deactivate-user-liberar">
                    {busy ? "Procesando..." : "Desactivar y liberar sus turnos (recomendado)"}
                  </button>
                  <button type="button" disabled={busy} onClick={() => run("solo_desactivar")} className="btn-ghost" style={{ padding: 10 }} data-testid="deactivate-user-solo">
                    Solo desactivar (los turnos quedan en el cronograma)
                  </button>
                </>
              )}
              <button type="button" onClick={onClose} style={{ border: "1px solid var(--invict-bone-3)", background: "transparent", borderRadius: 4, padding: 9, cursor: "pointer" }} data-testid="deactivate-user-cancel">Cancelar</button>
            </div>
            <p style={{ fontSize: 11, color: "var(--invict-text-muted)", margin: "10px 0 0" }}>
              Los turnos liberados quedan en "Turnos borrados recientemente" (se pueden restaurar) y como VACANTE en el Cronograma para asignar a otra persona.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default DeactivateUserModal;
